import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import LockIcon from '@mui/icons-material/Lock';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StandardDialog from '../../Modals/StandardDialog';
import FileUploaderComp, { FileUpload } from '../../Forms/FileUpload';
import User from '../../../interfaces/users.interface';
import { QCReviewItem } from '../../../interfaces/qcReviewData.interface';
import { ASSESSOR_ANSWER_COLORS } from '../../Themes/assessment';
import { CHIP_THEMES, formatQcDateTime, ACCENT_BLUE, ERROR_RED } from '../../Assessments/Workspace/qcTheme';
import { getSPDisposition, isRealSubControl } from './qcAttention';

// Mirrors services/surveyHelpers — inlined to stay off its axios/Amplify chain (same isolation as workspaceDerivations).
const FULLY_IMPLEMENTED = 'Fully Implemented';
const NOT_IMPLEMENTED = 'Not Implemented';
const NOT_APPLICABLE = 'Not Applicable';

// Read-only twin of the workspace's answer column, driven by the one-request review-data bundle — the modal never pays the full questionnaire load.

const Caption = styled('p')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  margin: '0 0 10px',
});

// Dashed like the workspace's locked read-only pill.
const OverallBadge = styled('span', {
  shouldForwardProp: prop => prop !== 'rgb' && prop !== 'tcolor',
})<{ rgb: string; tcolor: string }>(({ rgb, tcolor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '9px',
  height: '38px',
  padding: '0 16px',
  borderRadius: '6px',
  border: `1.5px dashed rgba(${rgb},0.55)`,
  background: `rgba(${rgb},0.04)`,
  fontSize: '0.9rem',
  fontWeight: 700,
  color: tcolor,
  '& > .MuiSvgIcon-root': { fontSize: '15px', opacity: 0.65 },
}));

const SpAnswerBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  height: '22px',
  padding: '0 10px',
  borderRadius: '11px',
  border: '1px dashed rgba(0,0,0,0.35)',
  fontSize: '0.65rem',
  fontWeight: 700,
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  color: 'rgba(0,0,0,0.6)',
  '& .MuiSvgIcon-root': { fontSize: '12px' },
});

const RingDot = styled('span')({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  border: '3.5px solid currentColor',
  flex: 'none',
});

const MatrixTable = styled('div')({
  marginTop: '16px',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  overflow: 'hidden',
});

const HeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '9px 14px',
  background: '#fafbfe',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
});

const ColLabel = styled('span', {
  shouldForwardProp: prop => prop !== 'w',
})<{ w?: number }>(({ w }) => ({
  width: w ? `${w}px` : undefined,
  flex: w ? 'none' : 1,
  fontSize: '0.68rem',
  letterSpacing: '0.8px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.55)',
  textTransform: 'uppercase',
}));

const ControlRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '9px 14px',
  '&:not(:last-of-type)': { borderBottom: '1px solid rgba(0,0,0,0.06)' },
});

const CellSlot = styled('span', {
  shouldForwardProp: prop => prop !== 'w',
})<{ w: number }>(({ w }) => ({
  width: `${w}px`,
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
}));

const DispositionChip = styled('span', {
  shouldForwardProp: prop => prop !== 'rgb' && prop !== 'tcolor',
})<{ rgb: string; tcolor: string }>(({ rgb, tcolor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: '22px',
  padding: '0 9px',
  borderRadius: '11px',
  fontSize: '0.7rem',
  fontWeight: 700,
  background: `rgba(${rgb},0.1)`,
  border: `1px solid rgba(${rgb},0.4)`,
  color: tcolor,
  whiteSpace: 'nowrap',
}));

const EmptyChip = styled('span')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.35)',
});

const ControlText = styled('span')({
  flex: 1,
  minWidth: 0,
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.8)',
  lineHeight: 1.45,
  paddingLeft: '20px',
});

const RemediationBadge = styled('span')({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  flex: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.68rem',
  fontWeight: 700,
  color: ERROR_RED,
  border: '1.5px solid rgba(198,40,40,0.6)',
  marginLeft: '10px',
});

const FindingsBox = styled('div')({
  marginTop: '18px',
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  background: '#fafbfe',
  padding: '12px 14px',
  fontSize: '0.875rem',
  lineHeight: 1.55,
  color: 'rgba(0,0,0,0.8)',
});

const NoneText = styled('p')({
  fontSize: '0.8125rem',
  fontStyle: 'italic',
  color: 'rgba(0,0,0,0.5)',
  margin: '8px 0 0',
});

const DetailsRow = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  gap: '14px',
  flexWrap: 'wrap',
  marginTop: '20px',
});

const DetailsBox = styled('div')({
  flex: '1 1 260px',
  minWidth: 0,
  border: '1px solid rgba(0,0,0,0.1)',
  borderRadius: '6px',
  background: '#fafbfe',
  padding: '10px 14px',
});

const DetailsText = styled('p')({
  fontSize: '0.8125rem',
  color: 'rgba(0,0,0,0.7)',
  margin: '6px 0 0',
  '&.none': { fontStyle: 'italic', color: 'rgba(0,0,0,0.5)' },
});

const CountButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  flex: 'none',
  background: '#fff',
  border: '1px solid rgba(25,118,210,0.5)',
  color: ACCENT_BLUE,
  borderRadius: '4px',
  padding: '8px 12px',
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { borderColor: ACCENT_BLUE, background: 'rgba(25,118,210,0.04)' },
  '&:disabled': { color: 'rgba(0,0,0,0.35)', borderColor: 'rgba(0,0,0,0.2)', cursor: 'default' },
  '& .MuiSvgIcon-root': { fontSize: '15px' },
});

const CommentEntry = styled('div')({
  padding: '12px 18px',
  '&:not(:last-of-type)': { borderBottom: '1px solid rgba(0,0,0,0.08)' },
});

const CommentMeta = styled('div')({
  fontSize: '0.75rem',
  color: 'rgba(0,0,0,0.55)',
  marginBottom: '4px',
  '& strong': { color: 'rgba(0,0,0,0.8)' },
});

const CommentText = styled('div')({
  fontSize: '0.875rem',
  color: 'rgba(0,0,0,0.85)',
  lineHeight: 1.5,
});

// Workspace per-row rule: the aggregate answer implies every control; only Partially Implemented reads per-control markings.
function assessorBucketFor(item: QCReviewItem, subControlId: number): string | undefined {
  if (item.answer === FULLY_IMPLEMENTED) return 'implemented';
  if (item.answer === NOT_IMPLEMENTED) return 'not_implemented';
  if (item.answer === NOT_APPLICABLE) return 'not_applicable';
  return item.assessorQuestionAnswers.find(aqa => aqa.answerId === subControlId)?.compensatingOrNotApplicable
    || undefined;
}

// SP overall label from the buckets, mirroring getSpOverallLabel: one answered bucket names it, any mix is Partially Implemented.
function spOverallLabel(item: QCReviewItem, realSubControlIds: number[]): string {
  const sp = item.spDisposition;
  if (!sp) return 'Not Answered';
  const buckets = [
    { label: FULLY_IMPLEMENTED, ids: sp.answer },
    { label: NOT_IMPLEMENTED, ids: sp.nonImplementedAnswers },
    { label: NOT_APPLICABLE, ids: sp.nonApplicableAnswers },
    { label: 'Partially Implemented', ids: sp.compensatingControlAnswers },
  ].filter(bucket => bucket.ids.some(id => realSubControlIds.includes(id)));
  if (!buckets.length) return 'Not Answered';
  if (buckets.length === 1 && buckets[0].label !== 'Partially Implemented') return buckets[0].label;
  return 'Partially Implemented';
}

export interface QcQuestionSnapshotProps {
  item: QCReviewItem;
}

export default function QcQuestionSnapshot({ item }: QcQuestionSnapshotProps): JSX.Element {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const overallLabel = item.answer || 'Not Answered';
  const overallColors = ASSESSOR_ANSWER_COLORS[overallLabel] || { rgb: '97,97,97', text: '#616161' };
  const subControls = item.question.subControls.filter(isRealSubControl);
  const evidenceFiles: FileUpload[] = item.evidence.map(file => ({
    id: file.id,
    file: file.document,
    date: file.createdAt,
    user: file.createdBy as unknown as User,
    isPublic: file.isPublic,
  }));
  const spOverall = spOverallLabel(item, subControls.map(subControl => subControl.id));
  const mirrorsSpAnswer = !!item.answer && item.answer === spOverall;

  return (
    <div>
      <Caption>Assessor Answer</Caption>
      <OverallBadge rgb={overallColors.rgb} tcolor={overallColors.text}>
        <RingDot />
        {overallLabel}
        {mirrorsSpAnswer ? (
          <SpAnswerBadge>
            <LockIcon />
            SP Answer
          </SpAnswerBadge>
        ) : <LockIcon />}
      </OverallBadge>
      {subControls.length > 0 && (
        <MatrixTable>
          <HeaderRow>
            <ColLabel w={78}>SP</ColLabel>
            <ColLabel w={78}>Assessor</ColLabel>
            <ColLabel sx={{ paddingLeft: '20px' }}>Control</ColLabel>
          </HeaderRow>
          {subControls.map(subControl => {
            const spBucket = getSPDisposition(item, subControl.id);
            const spTheme = spBucket ? CHIP_THEMES[spBucket] : undefined;
            const assessorBucket = assessorBucketFor(item, subControl.id);
            const assessorTheme = assessorBucket ? CHIP_THEMES[assessorBucket] : undefined;
            const needsRemediation = ['compensating_control', 'not_implemented'].includes(assessorBucket || '');
            return (
              <ControlRow key={subControl.id}>
                <CellSlot w={78}>
                  {spTheme
                    ? <DispositionChip rgb={spTheme.rgb} tcolor={spTheme.text}>{spTheme.label}</DispositionChip>
                    : <EmptyChip>—</EmptyChip>}
                </CellSlot>
                <CellSlot w={78}>
                  {assessorTheme
                    ? <DispositionChip rgb={assessorTheme.rgb} tcolor={assessorTheme.text}>{assessorTheme.label}</DispositionChip>
                    : <EmptyChip>—</EmptyChip>}
                </CellSlot>
                <ControlText>{subControl.text}</ControlText>
                {needsRemediation && (
                  <Tooltip title='Generates a remediation item'>
                    <RemediationBadge>R</RemediationBadge>
                  </Tooltip>
                )}
              </ControlRow>
            );
          })}
        </MatrixTable>
      )}
      <DetailsRow>
        <DetailsBox>
          <Caption sx={{ margin: 0 }}>SP Additional Details</Caption>
          {item.spAdditionalDetails
            ? <DetailsText>{item.spAdditionalDetails}</DetailsText>
            : <DetailsText className='none'>None provided.</DetailsText>}
        </DetailsBox>
        <CountButton
          type='button'
          disabled={!item.evidence.length}
          onClick={() => setEvidenceOpen(true)}
        >
          <AttachFileIcon />
          Evidence ({item.evidence.length})
        </CountButton>
        <CountButton
          type='button'
          disabled={!item.comments.length}
          onClick={() => setCommentsOpen(true)}
        >
          <ChatBubbleOutlineIcon />
          Comments ({item.comments.length})
        </CountButton>
      </DetailsRow>
      <Caption sx={{ margin: '22px 0 0' }}>Assessor Findings</Caption>
      {item.assessorFinding
        ? <FindingsBox>{item.assessorFinding}</FindingsBox>
        : <NoneText>No findings recorded.</NoneText>}
      <StandardDialog
        title={`Evidence for question: ${item.question.title}`}
        handleClose={() => setEvidenceOpen(false)}
        isOpen={evidenceOpen}
      >
        <FileUploaderComp
          disabled
          showCOPublicModal
          assessorVisible
          uploadType='evidence'
          fileList={evidenceFiles}
          callback={() => {}}
          dontShowUpload
          enablePublic
        />
      </StandardDialog>
      <Dialog
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        aria-label='SP answer comments'
        PaperProps={{ sx: { width: 560, maxWidth: '100%', borderRadius: '4px', padding: '8px 0' } }}
        maxWidth={false}
      >
        {item.comments.map(comment => (
          <CommentEntry key={comment.id}>
            <CommentMeta>
              <strong>{`${comment.user.firstName} ${comment.user.lastName}`.trim()}</strong>
              {' · '}
              {formatQcDateTime(comment.createdAt)}
            </CommentMeta>
            <CommentText>{comment.text}</CommentText>
          </CommentEntry>
        ))}
      </Dialog>
    </div>
  );
}
